import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HomePage } from './pages/HomePage';
import { SearchPage } from './pages/SearchPage';
import { PlayerPage } from './pages/PlayerPage';
import { ParentDashboard } from './pages/ParentDashboard';
import { Layout } from './components/Layout';
import { SessionPrompt } from './components/SessionPrompt';
import { TimeOverlay } from './components/TimeOverlay';
import { GoogleSignIn } from './components/GoogleSignIn';
import { useAppStore } from './store/appStore';
import { useGoogleAuth } from './store/googleAuthStore';

const CLOUD_HYDRATION_RETRY_MS = 60_000;
const CLOUD_REVALIDATION_INTERVAL_MS = 5 * 60_000;

function AppContent() {
    const { hasActiveSession, isTimeUp } = useAppStore();
    const [, setTick] = useState(0);
    const hasFetchedCloudRef = useRef<string | null>(null);
    const lastRevalidationRef = useRef(0);

    const {
        currentProfile,
        profiles,
        setCurrentProfile,
        fetchFromCloud,
        activateAccount,
        accountId: cachedAccountId,
        isCloudHydrated
    } = useAppStore();
    const isSignedIn = useGoogleAuth(state => state.isSignedIn);
    const isAuthLoading = useGoogleAuth(state => state.isLoading);
    const authenticatedAccountId = useGoogleAuth(state => state.user?.id || null);

    useEffect(() => {
        const accountIsReady = !isSignedIn || (
            !!authenticatedAccountId
            && cachedAccountId === authenticatedAccountId
            && isCloudHydrated
        );
        if (accountIsReady && !currentProfile && profiles.length > 0) {
            setCurrentProfile(profiles[0]);
        }
    }, [authenticatedAccountId, cachedAccountId, currentProfile, isCloudHydrated, isSignedIn, profiles, setCurrentProfile]);

    useEffect(() => {
        if (!isSignedIn || !authenticatedAccountId) {
            hasFetchedCloudRef.current = null;
            lastRevalidationRef.current = 0;
            return;
        }

        if (hasFetchedCloudRef.current === authenticatedAccountId) return;
        hasFetchedCloudRef.current = authenticatedAccountId;
        lastRevalidationRef.current = Date.now();
        activateAccount(authenticatedAccountId);
        void fetchFromCloud(authenticatedAccountId);
    }, [activateAccount, authenticatedAccountId, fetchFromCloud, isSignedIn]);

    useEffect(() => {
        if (!isSignedIn || !authenticatedAccountId || isCloudHydrated) return;
        const retry = window.setInterval(() => {
            void fetchFromCloud(authenticatedAccountId);
        }, CLOUD_HYDRATION_RETRY_MS);
        return () => window.clearInterval(retry);
    }, [authenticatedAccountId, fetchFromCloud, isCloudHydrated, isSignedIn]);

    useEffect(() => {
        if (!isSignedIn || !authenticatedAccountId || cachedAccountId !== authenticatedAccountId || !isCloudHydrated) {
            return;
        }

        const revalidate = () => {
            if (document.visibilityState === 'hidden') return;
            const now = Date.now();
            if (now - lastRevalidationRef.current < CLOUD_REVALIDATION_INTERVAL_MS) return;
            lastRevalidationRef.current = now;
            void fetchFromCloud(authenticatedAccountId);
        };

        window.addEventListener('focus', revalidate);
        document.addEventListener('visibilitychange', revalidate);
        const interval = window.setInterval(revalidate, CLOUD_REVALIDATION_INTERVAL_MS);
        return () => {
            window.removeEventListener('focus', revalidate);
            document.removeEventListener('visibilitychange', revalidate);
            window.clearInterval(interval);
        };
    }, [authenticatedAccountId, cachedAccountId, fetchFromCloud, isCloudHydrated, isSignedIn]);

    // Force re-render every second to update timer display
    useEffect(() => {
        const interval = setInterval(() => {
            setTick(t => t + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Check session status on every render (tick)
    const hasSession = hasActiveSession();
    const timeUp = hasSession && isTimeUp();

    // Keep the shared family cache behind authentication while D1 hydration is
    // pending. Google identity does not partition the underlying family data.
    if (isAuthLoading || (isSignedIn && (
        !authenticatedAccountId
        || cachedAccountId !== authenticatedAccountId
        || !isCloudHydrated
    ))) {
        return <div className="app-loading" role="status">Loading your family settings…</div>;
    }

    if (!isSignedIn) {
        return (
            <div className="app-loading app-family-connect" role="main">
                <div style={{ fontSize: '3rem' }}>☁️</div>
                <h1>Parent sign-in</h1>
                <p>Sign in to authenticate this device. Profiles, blocks, likes, and watch progress come from the same shared family cloud data regardless of which Google account is used.</p>
                <GoogleSignIn />
            </div>
        );
    }

    // No session yet → show prompt to set duration
    if (!hasSession) {
        return <SessionPrompt />;
    }

    // Time is up → show time's up screen ALWAYS
    if (timeUp) {
        return <TimeOverlay />;
    }

    // Session active and time remaining → show app
    return (
        <Routes>
            <Route path="/" element={<Layout />}>
                <Route index element={<HomePage />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="watch/:videoId" element={<PlayerPage />} />
                <Route path="parent" element={<ParentDashboard />} />
            </Route>
        </Routes>
    );
}

export function App() {
    return (
        <BrowserRouter>
            <AppContent />
        </BrowserRouter>
    );
}
